import React from "react";
import { observer } from "mobx-react-lite";
import { createPortal } from "react-dom";
import { AlertTriangle, Trash2, Power, Save, RefreshCw, AlertCircle } from "lucide-react";
import { confirmStore } from "../../stores/confirm.store";
import { Button } from "../design/Button";
import { cn } from "../../utils/cn";

const iconMap = {
    trash: Trash2,
    alert: AlertTriangle,
    power: Power,
    save: Save,
    refresh: RefreshCw,
};

const variantStyles = {
    danger: {
        iconBg: "bg-red-500/20",
        iconColor: "text-red-400",
        confirmBtn: "danger" as const,
    },
    warning: {
        iconBg: "bg-yellow-500/20", 
        iconColor: "text-yellow-400",
        confirmBtn: "primary" as const,
    },
    info: {
        iconBg: "bg-blue-500/20",
        iconColor: "text-blue-400",
        confirmBtn: "primary" as const,
    },
};

export const ConfirmModal = observer(() => {
    const { isOpen, options, handleConfirm, handleCancel } = confirmStore;

    if (!isOpen) return null;

    const Icon = options.icon ? iconMap[options.icon] : AlertCircle;
    const variant = variantStyles[options.variant || 'danger'];

    return createPortal(
        <div 
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-[9999] p-4 animate-in fade-in duration-150"
            onClick={handleCancel}
        >
            <div 
                className="bg-surface border border-line rounded-2xl w-full max-w-md shadow-2xl animate-in zoom-in-95 duration-200 overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="p-6 text-center">
                    <div className={cn(
                        "w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center",
                        variant.iconBg
                    )}>
                        <Icon size={32} className={variant.iconColor} />
                    </div>
                    
                    <h3 className="text-xl font-bold text-white mb-2">
                        {options.title}
                    </h3>
                    
                    <p className="text-fg-muted text-sm whitespace-pre-line">
                        {options.message}
                    </p>
                </div>

                <div className="flex gap-3 p-4 bg-black/40 border-t border-line">
                    <Button
                        variant="secondary"
                        className="flex-1"
                        onClick={handleCancel}
                    >
                        {options.cancelText}
                    </Button>
                    <Button
                        variant={variant.confirmBtn}
                        className="flex-1"
                        onClick={handleConfirm}
                    >
                        {options.confirmText}
                    </Button>
                </div>
            </div>
        </div>,
        document.body
    );
});
